import React from 'react';
import styled from 'styled-components';

import DiptychComponent from '$components/diptych';

import { childByName, getChildIndex, hasSpecificChildren } from './utils';
import { ErrorBlock, ErrorCode } from './error';
import { BrowserFrameProps } from '$components/browser-frame';

const DiptychBlockComponent = styled(DiptychComponent)`
  .media-item {
    height: auto;
  }
`;

interface DiptychProps {
  children: React.ReactNode;
  noDecoration?: boolean;
  title?: string;
}

function Diptych(props: DiptychProps) {
  const { children, noDecoration, title } = props;

  if (
    !hasSpecificChildren(children, ['DiptychMedia', 'DiptychContent']) &&
    !hasSpecificChildren(children, ['DiptychBrowserMedia', 'DiptychContent'])
  ) {
    return (
      <ErrorBlock>
        <p>
          The <strong>Diptych</strong> block requires{' '}
          <strong>Diptych.Media</strong> and <strong>Diptych.Content</strong>{' '}
          children. For example:
        </p>
        <div>
          <ErrorCode>
            {`<Diptych>
              <Diptych.Media src='' alt='' />
              <Diptych.Content>Something</Diptych.Content>
            </Diptych>`}
          </ErrorCode>
        </div>
      </ErrorBlock>
    );
  }

  const diptychMediaChild = childByName(
    children,
    'DiptychMedia'
  ) as React.ReactElement<DiptychMediaProps>;
  const diptychBrowserMediaChild = childByName(
    children,
    'DiptychBrowserMedia'
  ) as React.ReactElement<DiptychBrowserMediaProps>;

  const media = diptychMediaChild || diptychBrowserMediaChild;

  const content = childByName(children, 'DiptychContent') as React.ReactElement;

  const mediaIndex = Math.max(
    getChildIndex(children, 'DiptychMedia'),
    getChildIndex(children, 'DiptychBrowserMedia')
  );
  const type = mediaIndex === 0 ? 'alpha' : 'beta';

  const src = media.props.src;
  const caption = media.props.children;
  const imgSrc = typeof src === 'string' ? src : src.default;
  const browserMediaProps = diptychBrowserMediaChild
    ? {
        title: diptychBrowserMediaChild.props.title,
        url: diptychBrowserMediaChild.props.url,
        noShadow: diptychBrowserMediaChild.props.noShadow,
        noColor: diptychBrowserMediaChild.props.noColor
      }
    : undefined;

  const isVideo = !!imgSrc.toLowerCase().match(/\.(mp4|webm)$/);
  const isImage = !!imgSrc.toLowerCase().match(/\.(jpeg|jpg|gif|png)$/);

  if (!isVideo && !isImage) {
    return (
      <ErrorBlock>
        <p>Invalid media file type</p>
        <p>
          Supported video types: <code>mp4</code>, <code>webm</code>
        </p>
        <p>
          Supported image types: <code>jpg</code>, <code>gif</code>,{' '}
          <code>png</code>
        </p>
      </ErrorBlock>
    );
  }

  return (
    <DiptychBlockComponent
      noDecoration={noDecoration || !!caption || !!diptychBrowserMediaChild}
      layout={type}
      title={title}
      src={imgSrc}
      alt={media.props.alt || 'Image'}
      caption={caption}
      browserMediaProps={browserMediaProps}
    >
      {content}
    </DiptychBlockComponent>
  );
}

interface DiptychMediaProps {
  src: string | { default: string };
  alt?: string;
  children?: React.ReactNode;
}

function DiptychMedia() {
  return null;
}
DiptychMedia.displayName = 'DiptychMedia';

type DiptychBrowserMediaProps = DiptychMediaProps & BrowserFrameProps;

function DiptychBrowserMedia() {
  return null;
}
DiptychBrowserMedia.displayName = 'DiptychBrowserMedia';

function DiptychContent({ children }: { children: React.ReactNode }) {
  return children;
}
DiptychContent.displayName = 'DiptychContent';

Diptych.Media = DiptychMedia;
Diptych.BrowserMedia = DiptychBrowserMedia;
Diptych.Content = DiptychContent;

export { Diptych };
